/*
 * FILE: unified_ui/src/stores/gate.ts
 * PURPOSE: Pinia store for the Telegram channel subscription gate. Decides
 *          whether the SPA must show the subscribe screen and tracks membership
 *          status: 'unknown' (checking) | 'exempt' (non-telegram) | 'ok'
 *          (subscribed) | 'blocked' (not subscribed / check failed).
 *          FAIL-CLOSED: every launch performs a fresh auth + subscription check;
 *          a network/server error blocks the app — no caching, no fail-open,
 *          no rate limiting.
 * OWNS: gate state, initial status fetch, forced recheck, visibilitychange
 *       auto-recheck.
 * EXPORTS: useGateStore (state, check, recheck, dispose)
 * DOCS: .agents/reports/plan_subscribe-gate_2026-08-11.md
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useIdentityStore } from '@/stores/identity'
import { trackEvent } from '@/lib/trackEvent'
import { authHeaders } from '@/lib/sessionToken'

export type GateState = 'unknown' | 'exempt' | 'ok' | 'blocked'

interface GateStatusResponse {
  success: boolean
  required?: boolean
  subscribed?: boolean
}

async function fetchStatus(path: string, method: 'GET' | 'POST'): Promise<GateStatusResponse | null> {
  try {
    const res = await fetch(path, { method, credentials: 'include', headers: authHeaders() })
    if (!res.ok) return null
    return (await res.json()) as GateStatusResponse
  } catch (err: unknown) {
    console.warn('[gate] status fetch failed — fail-closed:', err instanceof Error ? err.message : String(err))
    return null
  }
}

export const useGateStore = defineStore('gate', () => {
  const identity = useIdentityStore()
  const state = ref<GateState>('unknown')

  function applyResult(status: GateStatusResponse | null) {
    const wasBlocked = state.value === 'blocked'
    if (!status?.success) {
      // Network/server error → fail-closed: block the app. The user must be
      // verified subscribed before ANY access; a broken check is a denial.
      state.value = 'blocked'
      return
    }
    if (!status.required) {
      state.value = 'exempt'
      return
    }
    state.value = status.subscribed ? 'ok' : 'blocked'
    // Path event: a blocked user just passed the wall («Я подписался» recheck
    // or the visibility-return auto-recheck confirmed membership).
    if (wasBlocked && state.value !== 'blocked') {
      trackEvent('subscribe-gate-passed')
    }
  }

  /**
   * Initial status check after auth. Non-telegram platforms are exempt without
   * a network call. The server NEVER caches: this is a fresh membership check
   * on every launch.
   */
  async function check() {
    if (identity.platform !== 'telegram') {
      state.value = 'exempt'
      return
    }
    applyResult(await fetchStatus('/api/gate/status', 'GET'))
  }

  /**
   * Fresh check — manual «Я подписался» button and the visibility-return auto
   * path. Same procedure as `check`, no throttling: every re-entry re-verifies.
   */
  async function recheck() {
    if (identity.platform !== 'telegram') {
      state.value = 'exempt'
      return
    }
    applyResult(await fetchStatus('/api/gate/recheck', 'POST'))
  }

  function onVisibilityChange() {
    if (document.visibilityState !== 'visible') return
    // The user may have joined/left the channel while the app was suspended —
    // always re-verify, every time.
    recheck()
  }

  document.addEventListener('visibilitychange', onVisibilityChange)

  // Reset to 'unknown' when the identity is dropped (session expiry) so a stale
  // 'blocked' state can never flash over the re-auth flow.
  watch(
    () => identity.actor,
    (actor) => {
      if (!actor && state.value !== 'unknown') state.value = 'unknown'
    },
  )

  function dispose() {
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }

  return { state, check, recheck, dispose }
})
